/**
 * ViewportBadge Component
 *
 * Displays a badge indicating which viewport(s) an issue was detected on.
 */

import { Monitor, Smartphone, Layers } from 'lucide-react';

export type ViewportType = 'desktop' | 'mobile' | 'both';

interface ViewportBadgeProps {
  viewport: ViewportType;
  size?: 'sm' | 'md';
}

const viewportConfig: Record<
  ViewportType,
  {
    icon: typeof Monitor;
    label: string;
    bgColor: string;
    textColor: string;
    borderColor: string;
  }
> = {
  desktop: {
    icon: Monitor,
    label: 'Desktop',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
  mobile: {
    icon: Smartphone,
    label: 'Mobile',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
  },
  both: {
    icon: Layers,
    label: 'All Viewports',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-300',
  },
};

export function ViewportBadge({ viewport, size = 'sm' }: ViewportBadgeProps) {
  const config = viewportConfig[viewport];
  const Icon = config.icon;

  const sizeClasses = size === 'sm' ? 'text-caption px-1.5 py-0.5' : 'text-body-sm px-2 py-1';

  const iconSize = size === 'sm' ? 12 : 14;

  return (
    <span
      className={`inline-flex items-center gap-1 ${sizeClasses} ${config.bgColor} ${config.textColor} border ${config.borderColor} rounded`}
    >
      <Icon size={iconSize} />
      {config.label}
    </span>
  );
}

export default ViewportBadge;
